import { observer } from 'mobx-react'
import React, { ReactElement, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import styled, { css, keyframes } from 'styled-components'
import Papa from 'papaparse'
import { addDays, eachDayOfInterval, endOfDay, format, parse, startOfDay, subDays } from 'date-fns'
import { PrimaryButton, SecondaryButton } from '../../components/Button'
import PageHeader from '../../components/PageHeader'
import Table from '../../components/Table'
import { WithdrawableAsset } from '../../components/AssetWithdrawalModal'
import Dropdown, { DropdownContentHeader, DropdownItem, DropdownTextInput } from '../../components/Dropdown'
import ExportBalanceForm from '../../components/ExportBalanceForm'
import LoadingBar from '../../components/LoadingBar'
import Modal from '../../components/Modal'
import ConvertController, { usesConvertPage } from '../../state/ConvertController'
import Treasury from '../../state/Treasury'
import { WINDOW_SIZE } from '../../util/Constants'
import { getMethod, getMethodByTicker, getPaymentMethods, ManagedPaymentMethod, methodNetworkName, methodTokenAddress } from '../../util/Managed'
import {
  useTestMode,
  formatCurrency,
  errorToast,
  successToast,
  shortAddress,
  formatAssetAmount,
  isStableSwapTicker,
  compareAssetAmountStrings,
  floorAssetAmount,
  trimZeros
} from '../../util/Util'
import {
  ISwapSupportedAsset,
  isSwapSupportedAsset
} from '../../util/OkxSwap'
import User from '../../state/User'
import PaymentMethodDisplay from '../../components/PaymentMethodDisplay'
import Radom, { IPaymentMethod, ISwapQuoteResponse, ManagedBalance } from '../../state/Radom'
import SwapTransactionIcon from '../../icons/SwapTransactionIcon'
import Tooltip from '../../components/Tooltip'
import LineChart, { ChartPoint } from '../../components/LineChart'
import CopyButton from '../../components/CopyButton'
import { TokenTitle } from '../Funds'
import {
  DashboardPage,
  InlinePill,
  PageActionRow,
  PageHeaderRow,
  SurfaceHeader,
  SummaryLabel,
  SummaryMeta,
  SummaryStrip,
  SummaryStripItem,
  SummaryValue,
  Surface,
  SurfaceTitle
} from '../../components/DashboardPrimitives'
import Gate from '../../components/Gate'
import { usePermissions } from '../../util/permissions'

const SwapStepFadeIn = keyframes`
  from { opacity: 0; transform: translateY(6px); }
  to   { opacity: 1; transform: translateY(0); }
`

const SwapSpinOnce = keyframes`
  from { transform: rotate(0deg); }
  to   { transform: rotate(180deg); }
`

const SwapPulse = keyframes`
  0%, 100% { opacity: 1; }
  50%       { opacity: 0.4; }
`

const SwapStepPanel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
  animation: ${SwapStepFadeIn} 0.18s ease;
`

const SwapFlipButton = styled.button<{ $spinning: boolean }>`
  align-items: center;
  background: #2A63FF;
  border: none;
  border-radius: 999px;
  color: white;
  cursor: pointer;
  display: inline-flex;
  justify-content: center;
  min-height: 36px;
  min-width: 36px;
  margin: 4px;
  padding: 0;
  transition: background 0.15s, transform 0.15s;

  &:hover { background: #1a53ef; transform: scale(1.05); }
  &:disabled { cursor: not-allowed; opacity: 0.4; }

  ${({ $spinning }) => $spinning && css`
    animation: ${SwapSpinOnce} 0.3s ease;
  `}
`

const SwapEstimatedValue = styled.span<{ $pulsing: boolean }>`
  color: var(--ink, #20242d);
  font-size: 23px;
  font-weight: 600;
  line-height: 1.06;
  ${({ $pulsing }) => $pulsing && css`
    animation: ${SwapPulse} 1s ease infinite;
  `}
`

const BalanceHeroGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(240px, 280px) minmax(0, 1fr);
  gap: 20px;
  align-items: start;

  @media (max-width: ${WINDOW_SIZE.TABLET}) {
    grid-template-columns: 1fr;
  }
`

const BalanceScopeTabs = styled.div`
  align-items: center;
  background: var(--surface-hover, #f4f6fa);
  border: 1px solid var(--border, #e8edf5);
  border-radius: 12px;
  display: inline-flex;
  gap: 2px;
  padding: 3px;
`

const BalanceScopeTab = styled.button<{ $active?: boolean, disabled?: boolean }>`
  background: ${({ $active }) => $active ? 'var(--surface, #ffffff)' : 'transparent'};
  border: 0;
  border-radius: 9px;
  box-shadow: ${({ $active }) => $active ? '0 1px 4px rgba(15, 23, 42, 0.08)' : 'none'};
  color: ${({ $active, disabled }) => disabled ? 'var(--text-faint, #98a1b2)' : $active ? 'var(--ink, #20242d)' : 'var(--text-muted, #687183)'};
  cursor: ${({ disabled }) => disabled ? 'default' : 'pointer'};
  font-family: var(--font-sans);
  font-size: 12.75px;
  font-weight: 500;
  line-height: 1.2;
  min-height: 32px;
  padding: 7px 12px;
  transition: 0.16s ease all;
  white-space: nowrap;
`

const CenteredEmptyState = styled.div`
  align-items: center;
  color: var(--text-soft, #7d8492);
  display: flex;
  flex-direction: column;
  font-size: 12.75px;
  gap: 5px;
  justify-content: center;
  line-height: 1.45;
  min-height: 150px;
  padding: 18px;
  text-align: center;
`

const EmptyStateTitle = styled.span`
  color: var(--ink, #20242d);
  font-size: 14px;
  font-weight: 500;
`

const isSamePaymentMethod = (a: IPaymentMethod, b: IPaymentMethod): boolean => {
  return (
    a.network === b?.network && (a.token ?? null) === (b?.token ?? null)
  )
}

const balanceMatchesPaymentMethod = (balance: ManagedBalance, method: IPaymentMethod): boolean => {
  if (balance.asset) {
    return getMethod(method.network, method.token).ticker.toLowerCase() === balance.asset.toLowerCase()
  }
  return isSamePaymentMethod({ network: balance.network ?? '', token: balance.token }, method)
}

const MIN_SWAP_SOURCE_FIAT_VALUE = 5

const managedMethodToPaymentMethod = (method: ManagedPaymentMethod): IPaymentMethod => ({
  network: method.hostChain?.name ?? method.name,
  token: method.hostChain?.tokenAddress
})

const methodMatchesSwapAssets = (
  method: ManagedPaymentMethod,
  assets: ISwapSupportedAsset[]
): boolean => {
  const paymentMethod = managedMethodToPaymentMethod(method)
  return isSwapSupportedAsset(paymentMethod.network, paymentMethod.token, assets)
}

const getSwapMethodLabel = (method: ManagedPaymentMethod): string =>
  method.currencyName ?? method.name

interface SwapParams {
  fromNetwork: string
  fromToken?: string
  toNetwork?: string
  toToken?: string
  amount: number
};

interface BalanceRow {
  source: ManagedBalance
  method: ManagedPaymentMethod
  displayName: string
  networkLabel: string
  ticker: string
  balance: number
  isFiat: boolean
  isTestnet: boolean
  conversion?: { value: number }
  fiatValue: number
  isPrivateTreasury: boolean
}

interface ExportedBalanceRow {
  date: string
  network: string
  token?: string
  ticker: string
  balance: string
}

type BalanceHistoryRangeKey = '7d' | '30d' | '90d' | '1y'
type BalanceScope = 'radom' | 'private'

const BALANCE_HISTORY_RANGES: Array<{
  key: BalanceHistoryRangeKey
  label: string
  days: number
}> = [
  { key: '7d', label: '7D', days: 7 },
  { key: '30d', label: '30D', days: 30 },
  { key: '90d', label: '90D', days: 90 },
  { key: '1y', label: '1Y', days: 365 }
]

const parseBalanceHistoryDate = (value: string): Date | undefined => {
  if (!value) return undefined

  const dailyDate = parse(value, 'dd/MM/yyyy', new Date())
  if (!Number.isNaN(dailyDate.getTime())) {
    return startOfDay(dailyDate)
  }

  const monthlyDate = parse(value, 'MM/yyyy', new Date())
  if (!Number.isNaN(monthlyDate.getTime())) {
    return startOfDay(monthlyDate)
  }

  return undefined
}

const toQuoteNumber = (value: number | string | undefined): number => {
  const n = typeof value === 'number' ? value : Number(value)
  return Number.isFinite(n) ? n : 0
}

const SwapQuoteBreakdown = ({
  quote,
  fromTicker,
  toTicker,
  fromAmount
}: {
  quote: ISwapQuoteResponse
  fromTicker: string
  toTicker: string
  fromAmount?: string
}): ReactElement => {
  const rate = toQuoteNumber(quote.rate)
  const receiveAmount = toQuoteNumber(quote.amount)
  const inputAmount = toQuoteNumber(fromAmount)
  const effectiveRate = inputAmount > 0 && receiveAmount > 0 ? receiveAmount / inputAmount : rate

  const row = (label: string, value: string): ReactElement => (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10 }}>
      <span style={{ color: 'var(--text-muted, #5f6675)', fontSize: 13 }}>{label}</span>
      <span style={{ color: 'var(--ink, #20242d)', fontSize: 13, fontWeight: 500, textAlign: 'right' }}>
        {value}
      </span>
    </div>
  )

  return (
    <>
      {row(
        'Rate',
        `1 ${fromTicker} ≈ ${formatAssetAmount(effectiveRate, toTicker)} ${toTicker}`
      )}
      <span style={{ color: 'var(--text-faint, #8a919e)', fontSize: 12 }}>All fees are included in the rate.</span>
    </>
  )
}

const performSwapEstimate = async ({
  fromNetwork,
  fromToken,
  toNetwork,
  toToken,
  amount
}: SwapParams): Promise<ISwapQuoteResponse> => {
  return await Radom.getSwapRates({
    from: {
      network: fromNetwork,
      token: fromToken
    },
    into: {
      network: toNetwork ?? '',
      token: toToken
    },
    amount
  })
}

const performSwap = async ({
  fromNetwork,
  fromToken,
  toNetwork,
  toToken,
  amount
}: SwapParams): Promise<void> => {
  await Radom.initiateSwap({
    from: {
      network: fromNetwork,
      token: fromToken
    },
    into: {
      network: toNetwork ?? '',
      token: toToken
    },
    amount
  })
}

const Overview = observer((): ReactElement => {
  const [testMode] = useTestMode()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const { has, isLoaded } = usePermissions()

  // The balance history chart and the export report are both built from GET /export/balance,
  // which needs ReadEvents. Swapping quotes *and* executes through POST /swap/quote and
  // POST /swap — both WriteSwap — while the asset pickers read GET /swap/supported_assets.
  const canReadEvents = has('ReadEvents')
  const canReadSwap = has('ReadSwap')
  const canWriteSwap = has('WriteSwap')

  const [exportBalanceModalOpen, setExportBalanceModalOpen] = useState(false)

  const [assetToConvertFrom, setAssetToConvertFrom] = useState<IPaymentMethod | null>(null)
  const [balanceHistory, setBalanceHistory] = useState<ChartPoint[]>([])
  const [balanceHistoryLoading, setBalanceHistoryLoading] = useState(false)
  const [balanceHistoryUnavailable, setBalanceHistoryUnavailable] = useState(false)
  // First/last real exported data points; the change stat is only computed
  // when at least two real points exist (never against a fabricated zero).
  const [balanceHistoryTrend, setBalanceHistoryTrend] = useState<{ first: number, last: number } | null>(null)
  const [selectedBalanceRange, setSelectedBalanceRange] = useState<BalanceHistoryRangeKey>('30d')
  const [activeBalanceScope, setActiveBalanceScope] = useState<BalanceScope>('radom')

  const [swapModalOpen, setSwapModalOpen] = useState(false)
  const [swapAmount, setSwapAmount] = useState<string>('')
  const [swapFromBalance, setSwapFromBalance] = useState<string>('')
  const [selectedSwapInto, setSelectedSwapInto] = useState<ManagedPaymentMethod | null>(null)
  const [swapEstimate, setSwapEstimate] = useState<ISwapQuoteResponse | null>(null)
  const [swapLoading, setSwapLoading] = useState(false)
  const [swapEstimating, setSwapEstimating] = useState(false)
  const [swapStep, setSwapStep] = useState<'compose' | 'confirm'>('compose')
  const [swapSourceQuery, setSwapSourceQuery] = useState('')
  const [swapTargetQuery, setSwapTargetQuery] = useState('')
  const [swapEstimateMessage, setSwapEstimateMessage] = useState('')
  const [flipSpinning, setFlipSpinning] = useState(false)
  const [okxSwapAssets, setOkxSwapAssets] = useState<ISwapSupportedAsset[]>([])
  const [okxSwapTargets, setOkxSwapTargets] = useState<ISwapSupportedAsset[]>([])
  const swapAmountInputRef = useRef<HTMLInputElement>(null)
  // Monotonic id for quote requests: only the newest request may write
  // state, so a slow response for an old amount can never overwrite the
  // quote for what the user actually typed.
  const swapEstimateSeq = useRef(0)
  const defaultCurrency = User.organization?.defaultCurrency ?? 'USD'
  const isMultiLegSwapEnabled = !!User.organization?.features?.isMultiLegSwapEnabled
  const activeBalanceRange = BALANCE_HISTORY_RANGES.find(range => range.key === selectedBalanceRange) ??
    BALANCE_HISTORY_RANGES[1]

  useEffect(() => {
    Treasury.loadBalances()
  }, [])

  useEffect(() => {
    if (!isLoaded || !canReadSwap || !User.organization?.features?.isConversionEnabled) {
      return
    }

    const fetchOkxSwapAssets = async (): Promise<void> => {
      try {
        const response = await Radom.getSwapSupportedAssets()
        setOkxSwapAssets(response.assets)
      } catch (err) {
        console.error('Failed to load OKX swap assets', err.response?.data?.error || err.message)
      }
    }

    fetchOkxSwapAssets()
  }, [User.organization?.features?.isConversionEnabled, isLoaded, canReadSwap])

  useEffect(() => {
    if (!canReadSwap || !assetToConvertFrom || okxSwapAssets.length === 0) {
      setOkxSwapTargets([])
      return
    }

    let cancelled = false

    const fetchOkxSwapTargets = async (): Promise<void> => {
      try {
        const response = await Radom.getSwapSupportedTargets({
          network: assetToConvertFrom.network,
          token: assetToConvertFrom.token ?? undefined
        })
        if (!cancelled) {
          setOkxSwapTargets(response.assets)
        }
      } catch (err) {
        console.error('Failed to load OKX swap targets', err.response?.data?.error || err.message)
        if (!cancelled) {
          setOkxSwapTargets([])
        }
      }
    }

    fetchOkxSwapTargets()

    return () => {
      cancelled = true
    }
  }, [assetToConvertFrom, okxSwapAssets, canReadSwap])

  useEffect(() => {
    if (!selectedSwapInto || okxSwapTargets.length === 0) {
      return
    }

    if (!methodMatchesSwapAssets(selectedSwapInto, okxSwapTargets)) {
      setSelectedSwapInto(null)
      setSwapEstimate(null)
      setSwapEstimateMessage('')
    }
  }, [okxSwapTargets, selectedSwapInto])

  useEffect(() => {
    if (!isLoaded || !canReadEvents || Treasury.priceQuotes.length <= 0) {
      return
    }

    let cancelled = false

    const loadBalanceHistory = async (): Promise<void> => {
      setBalanceHistoryLoading(true)
      try {
        const historyEnd = endOfDay(new Date())
        const historyStart = startOfDay(subDays(historyEnd, activeBalanceRange.days - 1))

        // The export endpoint caps a single request at 31 days, so fetch
        // longer ranges sequentially in <=30-day chunks and merge them. A
        // single failed chunk fails the whole load and degrades to the
        // "history unavailable" note below.
        const csvChunks: string[] = []
        let chunkStart = historyStart
        while (chunkStart.getTime() <= historyEnd.getTime()) {
          if (cancelled) return

          const chunkCap = endOfDay(addDays(chunkStart, 29))
          const chunkEnd = chunkCap.getTime() < historyEnd.getTime() ? chunkCap : historyEnd
          csvChunks.push(await Radom.exportBalance(
            chunkStart.toISOString(),
            chunkEnd.toISOString(),
            'daily'
          ))
          chunkStart = startOfDay(addDays(chunkEnd, 1))
        }

        const totalsByDate = new Map<number, number>()
        csvChunks.forEach((csv) => {
          const parsed = Papa.parse<ExportedBalanceRow>(csv, {
            header: true,
            skipEmptyLines: true
          })

          parsed.data.forEach((row) => {
            const parsedDate = parseBalanceHistoryDate(row.date)
            if (!parsedDate) return

            const method = getMethod(row.network, row.token)
            const isVisibleInCurrentMode = testMode ? !!method.isTestnet : !method.isTestnet
            if (!isVisibleInCurrentMode) return

            const numericBalance = Number(row.balance)
            if (Number.isNaN(numericBalance)) return

            const isFiat = row.network === 'Fiat' || !!method.isFiat
            const conversion = Treasury.priceQuotes.find(p => p.from.toLowerCase() === row.ticker.toLowerCase())
            const estimatedValue = isFiat && row.ticker === defaultCurrency
              ? numericBalance
              : conversion
                ? numericBalance * Number(conversion.value)
                : 0

            const dayKey = parsedDate.getTime()
            totalsByDate.set(dayKey, (totalsByDate.get(dayKey) ?? 0) + estimatedValue)
          })
        })

        let lastKnownTotal = 0
        const filledSeries = eachDayOfInterval({ start: historyStart, end: historyEnd }).map((day) => {
          const dayKey = startOfDay(day).getTime()
          const dayValue = totalsByDate.get(dayKey)
          if (typeof dayValue === 'number') {
            lastKnownTotal = dayValue
          }

          return {
            x: dayKey,
            y: lastKnownTotal
          }
        })

        const realDayKeys = Array.from(totalsByDate.keys()).sort((a, b) => a - b)

        if (!cancelled) {
          if (realDayKeys.length === 0) {
            setBalanceHistory([])
            setBalanceHistoryTrend(null)
            setBalanceHistoryUnavailable(true)
          } else {
            setBalanceHistory(filledSeries)
            setBalanceHistoryUnavailable(false)
            setBalanceHistoryTrend(realDayKeys.length >= 2
              ? {
                first: Math.max(totalsByDate.get(realDayKeys[0]) ?? 0, 0),
                last: Math.max(totalsByDate.get(realDayKeys[realDayKeys.length - 1]) ?? 0, 0)
              }
              : null)
          }
        }
      } catch (err) {
        console.error('Failed to load balance history', err)
        if (!cancelled) {
          setBalanceHistory([])
          setBalanceHistoryTrend(null)
          setBalanceHistoryUnavailable(true)
        }
      } finally {
        if (!cancelled) {
          setBalanceHistoryLoading(false)
        }
      }
    }

    loadBalanceHistory()

    return () => {
      cancelled = true
    }
  }, [testMode, defaultCurrency, Treasury.priceQuotes.length, activeBalanceRange.days, isLoaded, canReadEvents])

  const onOpenSwap = (w: WithdrawableAsset): void => {
    setAssetToConvertFrom(w)
    setSwapAmount('')
    setSwapFromBalance(w.maxAmount)
    setSelectedSwapInto(null)
    setSwapEstimate(null)
    setSwapStep('compose')
    setSwapSourceQuery('')
    setSwapTargetQuery('')
    setSwapEstimateMessage('')
    setSwapModalOpen(true)
  }

  useEffect(() => {
    if (!swapModalOpen || swapStep !== 'compose') {
      return
    }

    const focusTimer = window.setTimeout(() => {
      swapAmountInputRef.current?.focus()
      swapAmountInputRef.current?.select()
    }, 0)

    return () => window.clearTimeout(focusTimer)
  }, [swapModalOpen, swapStep])

  const swapSourceMethods = useMemo(
    () => {
      const methods = getPaymentMethods()
        .filter(m =>
          m.isSwapEnabled &&
          ((testMode && m.isTestnet) || (!testMode && !m.isTestnet)) &&
          !m.isFiat &&
          !m.isHidden &&
          !m.isDisabled &&
          (okxSwapAssets.length === 0 || methodMatchesSwapAssets(m, okxSwapAssets))
        )
        .sort((a, b) => getSwapMethodLabel(a).localeCompare(getSwapMethodLabel(b)))
      if (!User.organization?.features?.isUnifiedTreasuryEnabled) {
        return methods
      }
      const seen = new Set<string>()
      return methods.filter(m => {
        const key = m.ticker.toLowerCase()
        if (seen.has(key)) {
          return false
        }
        seen.add(key)
        return true
      })
    },
    [testMode, okxSwapAssets]
  )

  const swapFromMethod = assetToConvertFrom ? getMethod(assetToConvertFrom.network, assetToConvertFrom.token) : null
  const swapFromTicker = swapFromMethod?.ticker
  const swapSourceIsStable = isStableSwapTicker(swapFromTicker)

  const swapTargetMethods = useMemo(
    () => swapSourceMethods
      .filter(m => !assetToConvertFrom || !isSamePaymentMethod(managedMethodToPaymentMethod(m), assetToConvertFrom))
      .filter(m => {
        if (!User.organization?.features?.isUnifiedTreasuryEnabled || !assetToConvertFrom) {
          return true
        }
        return getMethod(assetToConvertFrom.network, assetToConvertFrom.token).ticker !== m.ticker
      })
      .filter(m => {
        if (okxSwapAssets.length === 0) {
          return true
        }
        if (!assetToConvertFrom) {
          return methodMatchesSwapAssets(m, okxSwapAssets)
        }
        return okxSwapTargets.length > 0 && methodMatchesSwapAssets(m, okxSwapTargets)
      })
      .filter(m => isMultiLegSwapEnabled || swapSourceIsStable || isStableSwapTicker(m.ticker)),
    [assetToConvertFrom, swapSourceMethods, isMultiLegSwapEnabled, swapSourceIsStable, okxSwapAssets, okxSwapTargets]
  )

  const maxSwappableAmount = swapFromTicker
    ? floorAssetAmount(swapFromTicker, swapFromBalance || '0')
    : trimZeros(swapFromBalance || '0')
  const selectedSwapIntoMethod = selectedSwapInto ? managedMethodToPaymentMethod(selectedSwapInto) : null

  const recalculateSwapEstimate = async (
    nextAmount: string,
    nextInto: ManagedPaymentMethod | null,
    fromAsset: IPaymentMethod | null = assetToConvertFrom
  ): Promise<void> => {
    const fromMethod = fromAsset ? getMethod(fromAsset.network, fromAsset.token) : null
    const fromConversion = fromMethod
      ? Treasury.priceQuotes.find(p => p.from.toLowerCase() === fromMethod.ticker.toLowerCase())
      : undefined

    if (!fromAsset || !nextInto || !fromConversion) {
      swapEstimateSeq.current++
      setSwapEstimate(null)
      setSwapEstimateMessage('')
      setSwapEstimating(false)
      return
    }

    const numeric = Number(nextAmount)
    if (!nextAmount || Number.isNaN(numeric) || numeric <= 0) {
      swapEstimateSeq.current++
      setSwapEstimate(null)
      setSwapEstimateMessage('')
      setSwapEstimating(false)
      return
    }

    const fiatValue = numeric * fromConversion.value
    if (fiatValue < 0) {
      swapEstimateSeq.current++
      setSwapEstimate(null)
      setSwapEstimateMessage('')
      setSwapEstimating(false)
      return
    }

    const seq = ++swapEstimateSeq.current
    setSwapEstimating(true)
    setSwapEstimateMessage('')
    try {
      const estimate = await performSwapEstimate({
        fromNetwork: fromAsset.network,
        fromToken: fromAsset.token,
        toNetwork: nextInto?.hostChain?.name ?? nextInto?.name,
        toToken: nextInto?.hostChain?.tokenAddress ?? undefined,
        amount: numeric
      })
      if (seq !== swapEstimateSeq.current) return
      setSwapEstimate(estimate)
    } catch (err) {
      if (seq !== swapEstimateSeq.current) return
      setSwapEstimate(null)
      setSwapEstimateMessage(err.response?.data?.error || 'Estimate unavailable for this pair right now.')
    } finally {
      if (seq === swapEstimateSeq.current) {
        setSwapEstimating(false)
      }
    }
  }

  const toBalanceRows = (balances: ManagedBalance[]): BalanceRow[] => balances
    .filter(b => Number(b.balance) > 0)
    .map((b): BalanceRow => {
      const unifiedTicker = b.asset
      const method = unifiedTicker
        ? (getMethodByTicker(unifiedTicker) ?? {
          name: unifiedTicker,
          ticker: unifiedTicker,
          logo: undefined
        })
        : getMethod(b.network ?? '', b.token)
      const isFiat = (!unifiedTicker && b.network === 'Fiat') || !!method.isFiat
      const ticker = unifiedTicker ?? (isFiat ? (b.token ?? method.ticker) : method.ticker)
      const isPrivateTreasury = !!b.treasuryId
      const conversion = Treasury.priceQuotes
        .find(p => p.from.toLowerCase() === ticker.toLowerCase())
      const balance = Number(b.balance)
      const fiatValue = isFiat && ticker === defaultCurrency
        ? balance
        : conversion
          ? balance * conversion.value
          : 0

      return {
        source: b,
        method,
        displayName: isFiat ? `${ticker} balance` : method.currencyName ?? method.name,
        networkLabel: isFiat || unifiedTicker ? ticker : `${ticker} on ${method.hostChain?.name ?? method.name}`,
        ticker,
        balance,
        isFiat,
        isTestnet: !!method.isTestnet,
        conversion,
        fiatValue,
        isPrivateTreasury
      }
    })
    .filter(row => testMode ? row.isTestnet : !row.isTestnet)
    .sort((a, b) => b.fiatValue - a.fiatValue)

  const defaultBalanceRows = toBalanceRows(Treasury.managedBalances)
  const privateBalanceRows = toBalanceRows(Treasury.privateManagedBalances)
  const balanceRows = activeBalanceScope === 'private' ? privateBalanceRows : defaultBalanceRows
  // Currency-first presentation: one row per asset, networks as a detail.
  const groupedBalanceRows = useMemo(() => {
    const order: string[] = []
    const groups: Record<string, BalanceRow[]> = {}
    for (const row of balanceRows) {
      const key = `${row.ticker}${row.isPrivateTreasury ? `:${row.source.treasuryId ?? ''}` : ''}`
      if (!groups[key]) {
        groups[key] = []
        order.push(key)
      }
      groups[key].push(row)
    }
    return order.map(key => {
      const rows = groups[key]
      return {
        key,
        rows,
        first: rows[0],
        totalBalance: rows.reduce((sum, r) => sum + r.balance, 0),
        totalFiatValue: rows.reduce((sum, r) => sum + r.fiatValue, 0),
        conversion: rows.find(r => r.conversion)?.conversion,
        anyFiat: rows.some(r => r.isFiat)
      }
    })
  }, [balanceRows])
  const emptyFundsTitle = activeBalanceScope === 'private' ? 'No private treasury funds yet' : 'No funds yet'
  const emptyFundsDescription = activeBalanceScope === 'private'
    ? 'Private treasury deposits will appear here once available.'
    : 'Deposits and payments will appear here once settled.'
  const emptyAllocationTitle = balanceRows.length > 0 ? 'Value unavailable' : emptyFundsTitle
  const emptyAllocationDescription = balanceRows.length > 0
    ? 'Exchange-rate data is still loading for these balances.'
    : emptyFundsDescription

  const selectedSwapIntoBalanceRow = selectedSwapIntoMethod
    ? defaultBalanceRows.find((row) => balanceMatchesPaymentMethod(row.source, selectedSwapIntoMethod))
    : undefined
  const eligibleSwapSourceMethods = useMemo(() => {
    const fiatValueByMethod = new Map<string, number>()
    for (const row of defaultBalanceRows) {
      if (row.isPrivateTreasury || row.isFiat) {
        continue
      }
      const key = row.source.asset
        ? row.source.asset.toLowerCase()
        : `${row.source.network}:${row.source.token ?? ''}`
      fiatValueByMethod.set(key, row.fiatValue)
    }

    return swapSourceMethods.filter((method) => {
      const paymentMethod = managedMethodToPaymentMethod(method)
      const key = User.organization?.features?.isUnifiedTreasuryEnabled
        ? method.ticker.toLowerCase()
        : `${paymentMethod.network}:${paymentMethod.token ?? ''}`
      return (fiatValueByMethod.get(key) ?? 0) >= MIN_SWAP_SOURCE_FIAT_VALUE
    })
  }, [defaultBalanceRows, swapSourceMethods])
  const filteredSwapSourceMethods = eligibleSwapSourceMethods.filter((method) => {
    if (!swapSourceQuery.trim()) {
      return true
    }

    const normalizedQuery = swapSourceQuery.trim().toLowerCase()
    return getSwapMethodLabel(method).toLowerCase().includes(normalizedQuery) ||
      method.ticker.toLowerCase().includes(normalizedQuery) ||
      method.hostChain?.name.toLowerCase().includes(normalizedQuery)
  })
  const filteredSwapTargetMethods = swapTargetMethods.filter((method) => {
    if (!swapTargetQuery.trim()) {
      return true
    }

    const normalizedQuery = swapTargetQuery.trim().toLowerCase()
    return getSwapMethodLabel(method).toLowerCase().includes(normalizedQuery) ||
      method.ticker.toLowerCase().includes(normalizedQuery) ||
      method.hostChain?.name.toLowerCase().includes(normalizedQuery)
  })
  const hasExecutableSwapBalance = compareAssetAmountStrings(maxSwappableAmount, '0') > 0
  const canReviewSwap = Boolean(
    !swapLoading &&
    !swapEstimating &&
    assetToConvertFrom &&
    selectedSwapInto &&
    swapEstimate &&
    swapAmount &&
    hasExecutableSwapBalance &&
    compareAssetAmountStrings(swapAmount, '0') > 0 &&
    compareAssetAmountStrings(swapAmount, swapFromBalance || '0') <= 0
  )
  const swapEstimatedAmount = swapEstimate != null ? toQuoteNumber(swapEstimate.amount) : undefined

  const assetSummaries = Object.values(balanceRows.reduce<Record<string, {
    ticker: string
    fiatValue: number
  }>>((assets, row) => {
    const current = assets[row.ticker] ?? {
      ticker: row.ticker,
      fiatValue: 0
    }

    assets[row.ticker] = {
      ...current,
      fiatValue: current.fiatValue + row.fiatValue
    }

    return assets
  }, {})).sort((a, b) => b.fiatValue - a.fiatValue)
  const totalEstimatedValue = assetSummaries.reduce((total, row) => total + row.fiatValue, 0)
  const displayBalanceHistory = activeBalanceScope === 'private'
    ? balanceRows.length > 0
      ? [{ x: new Date().getTime(), y: Math.max(totalEstimatedValue, 0) }]
      : []
    : balanceHistory.map(point => ({ ...point, y: Math.max(point.y, 0) }))
  // Change stat is only shown when the history export produced at least two
  // real data points; otherwise render "—" instead of a fabricated change.
  const balanceTrendDelta: number | undefined = activeBalanceScope === 'private'
    ? 0
    : balanceHistoryTrend
      ? balanceHistoryTrend.last - balanceHistoryTrend.first
      : undefined
  const balanceTrendDeltaPct = activeBalanceScope !== 'private' && balanceHistoryTrend && balanceHistoryTrend.first > 0
    ? (balanceHistoryTrend.last - balanceHistoryTrend.first) / balanceHistoryTrend.first
    : undefined
  const topThreeEstimatedValue = assetSummaries.slice(0, 3).reduce((total, row) => total + row.fiatValue, 0)
  const topThreeShare = totalEstimatedValue > 0 ? topThreeEstimatedValue / totalEstimatedValue : 0
  const topAssetSummary = assetSummaries[0]
  const balanceHistoryWindowLabel = activeBalanceScope === 'private'
    ? 'Current private treasury balances'
    : displayBalanceHistory.length > 0
      ? `${format(new Date(displayBalanceHistory[0].x), 'MMM d')} - ${format(new Date(displayBalanceHistory[displayBalanceHistory.length - 1].x), 'MMM d, yyyy')}`
      : activeBalanceRange.label
  const allocationPalette = ['#3464e6', '#15957b', '#7c3aed', '#0891b2', '#f59e0b', '#94a3b8']
  const allocationRows = assetSummaries
    .filter(row => row.fiatValue > 0)
    .slice(0, 5)
    .map((row, index) => ({
      ...row,
      color: allocationPalette[index % allocationPalette.length],
      share: totalEstimatedValue > 0 ? row.fiatValue / totalEstimatedValue : 0
    }))
  const remainingAllocationValue = assetSummaries
    .slice(5)
    .reduce((total, row) => total + row.fiatValue, 0)
  if (remainingAllocationValue > 0) {
    allocationRows.push({
      ticker: 'Other',
      fiatValue: remainingAllocationValue,
      color: allocationPalette[allocationPalette.length - 1],
      share: totalEstimatedValue > 0 ? remainingAllocationValue / totalEstimatedValue : 0
    })
  }

  const flipSwapPair = async (): Promise<void> => {
    if (!selectedSwapInto || !swapFromMethod) {
      return
    }

    const nextSource = managedMethodToPaymentMethod(selectedSwapInto)
    const nextTarget = swapFromMethod
    const nextBalance = selectedSwapIntoBalanceRow?.source.balance ?? '0'
    const nextAmount = !swapAmount
      ? ''
      : Number(nextBalance) > 0 && Number(swapAmount) > Number(nextBalance)
        ? String(nextBalance)
        : swapAmount

    setAssetToConvertFrom(nextSource)
    setSwapFromBalance(String(nextBalance))
    setSelectedSwapInto(nextTarget)
    setSwapAmount(nextAmount)
    setSwapEstimate(null)
    setSwapEstimateMessage('')
    setSwapStep('compose')
    setSwapSourceQuery('')
    setSwapTargetQuery('')

    if (nextAmount) {
      await recalculateSwapEstimate(nextAmount, nextTarget, nextSource)
    }
  }

  const renderSwapMethodOption = (method: ManagedPaymentMethod): ReactElement => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, minHeight: 28, minWidth: 0 }}>
      <PaymentMethodDisplay
        method={managedMethodToPaymentMethod(method)}
        paymentMethod={method}
        excludeName
        size={22}
      />
      <div style={{ display: 'flex', flexDirection: 'column', gap: 1, minWidth: 0 }}>
        <span style={{ color: 'var(--ink, #20242d)', fontSize: 12.75, fontWeight: 500 }}>{method.ticker}</span>
        <span style={{ color: 'var(--icon-muted, #747894)', fontSize: 11.5, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {getSwapMethodLabel(method)}
        </span>
      </div>
    </div>
  )

  const swappableDefaultRow = useMemo(() => {
    if (!User.organization?.features?.isConversionEnabled) {
      return null
    }
    return defaultBalanceRows
      .filter((row) =>
        !row.isPrivateTreasury &&
        !row.isFiat &&
        row.method.isSwapEnabled &&
        row.fiatValue >= MIN_SWAP_SOURCE_FIAT_VALUE &&
        (okxSwapAssets.length === 0 || methodMatchesSwapAssets(row.method, okxSwapAssets))
      )
      .sort((a, b) => (b.fiatValue ?? 0) - (a.fiatValue ?? 0))[0] ?? null
  }, [defaultBalanceRows, okxSwapAssets])

  useEffect(() => {
    if (searchParams.get('convert') !== '1') return

    const next = new URLSearchParams(searchParams)
    next.delete('convert')
    setSearchParams(next, { replace: true })

    if (usesConvertPage()) {
      navigate('/convert')
      return
    }
    ConvertController.open()
  }, [searchParams])

  const withdrawableBalanceAssets: WithdrawableAsset[] = balanceRows
    .filter(row => !row.isFiat)
    .map(row => ({
      network: row.source.network ?? methodNetworkName(row.method),
      token: row.source.token ?? methodTokenAddress(row.method),
      asset: row.source.asset,
      maxAmount: row.source.balance,
      treasuryId: row.source.treasuryId
    }))

  return <DashboardPage>
    <PageHeaderRow>
      <PageHeader
        title='Balances' />
      <PageActionRow>
        <Gate permission='ReadEvents'>
          <SecondaryButton onClick={() => setExportBalanceModalOpen(true)}>
            Export report
          </SecondaryButton>
        </Gate>
      </PageActionRow>
    </PageHeaderRow>

    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
      <BalanceScopeTabs>
        <BalanceScopeTab
          $active={activeBalanceScope === 'radom'}
          onClick={() => setActiveBalanceScope('radom')}>
          Radom treasury
        </BalanceScopeTab>
      </BalanceScopeTabs>
    </div>

    <Surface style={{ gap: 16, padding: '18px 20px' }}>
      <BalanceHeroGrid>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 11, minWidth: 0 }}>
          <SurfaceHeader style={{ gap: 9 }}>
            <div style={{ minWidth: 0 }}>
              <SurfaceTitle>{activeBalanceScope === 'private' ? 'Private treasury balance' : 'Available balance'}</SurfaceTitle>
            </div>
            <InlinePill $tone={activeBalanceScope === 'private' ? 'blue' : testMode ? 'amber' : 'green'}>
              {activeBalanceScope === 'private' ? 'Private treasury' : testMode ? 'Test mode' : 'Live balances'}
            </InlinePill>
          </SurfaceHeader>
          <SummaryValue style={{ fontSize: 36, lineHeight: 1.04 }}>
            {Treasury.loading
              ? <LoadingBar style={{ height: 38, width: 180 }} />
              : formatCurrency(totalEstimatedValue, defaultCurrency)}
          </SummaryValue>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            {typeof balanceTrendDelta === 'number'
              ? <InlinePill $tone={balanceTrendDelta >= 0 ? 'green' : 'amber'}>
                {balanceTrendDelta >= 0 ? '+' : '-'}
                {formatCurrency(Math.abs(balanceTrendDelta), defaultCurrency)}
              </InlinePill>
              : <span style={{ color: 'var(--text-soft, #7d8492)', fontSize: 12.5, lineHeight: 1.4 }}>—</span>}
            {typeof balanceTrendDeltaPct === 'number' &&
                <span style={{ color: 'var(--text-soft, #7d8492)', fontSize: 12.5, lineHeight: 1.4 }}>
                  {`${balanceTrendDeltaPct >= 0 ? '+' : ''}${(balanceTrendDeltaPct * 100).toFixed(1)}% across ${activeBalanceRange.label}`}
                </span>}
          </div>
          <SummaryMeta>{balanceHistoryWindowLabel}</SummaryMeta>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 11, minWidth: 0 }}>
          {activeBalanceScope !== 'private' && canReadEvents && <div style={{ alignItems: 'center', display: 'flex', flexWrap: 'wrap', gap: 8, justifyContent: 'flex-end' }}>
            {BALANCE_HISTORY_RANGES.map((range) =>
              <SecondaryButton
                key={range.key}
                onClick={() => setSelectedBalanceRange(range.key)}
                style={{
                  background: range.key === selectedBalanceRange ? '#eff4ff' : 'var(--surface, #ffffff)',
                  borderColor: range.key === selectedBalanceRange ? '#d6e1ff' : 'var(--border, #e8edf5)',
                  color: range.key === selectedBalanceRange ? '#2447a6' : 'var(--text-muted, #556072)',
                  borderRadius: 999,
                  fontSize: 11.5,
                  minHeight: 29,
                  padding: '5px 10px'
                }}>
                {range.label}
              </SecondaryButton>
            )}
          </div>}

          <div style={{ minWidth: 0 }}>
            {!Treasury.loading && balanceRows.length === 0
              ? <CenteredEmptyState style={{ minHeight: 202 }}>
                <EmptyStateTitle>{emptyFundsTitle}</EmptyStateTitle>
                <span>{emptyFundsDescription}</span>
              </CenteredEmptyState>
              : activeBalanceScope !== 'private' && !canReadEvents
                ? <CenteredEmptyState style={{ minHeight: 202 }}>
                  <EmptyStateTitle>You don&apos;t have permission to access balance history</EmptyStateTitle>
                </CenteredEmptyState>
                : activeBalanceScope !== 'private' && !balanceHistoryLoading && balanceHistoryUnavailable
                  ? <CenteredEmptyState style={{ minHeight: 202 }}>
                    <span style={{ color: 'var(--text-soft, #7d8492)', fontSize: 12.5 }}>Balance history unavailable</span>
                  </CenteredEmptyState>
                  : <LineChart
                    accentColor='#3464e6'
                    data={displayBalanceHistory}
                    height={202}
                    isLoading={activeBalanceScope === 'private' ? Treasury.loading : balanceHistoryLoading}
                    showYAxis
                    formatXAxis={(value: number) => format(new Date(value), 'MMM d')}
                    formatTooltipX={(value: number) => format(new Date(value), 'MMM d, yyyy')}
                    formatYAxis={(value: number) => formatCurrency(value, defaultCurrency)}
                    formatTooltipY={(value: number) => formatCurrency(value, defaultCurrency)}
                    minTickGap={90}
                  />}
          </div>
        </div>
      </BalanceHeroGrid>

      <SummaryStrip style={{ gridTemplateColumns: 'repeat(3, minmax(0, 1fr))' }}>
        {[
          {
            label: `${activeBalanceRange.label} change`,
            value: Treasury.loading
              ? 'loading'
              : typeof balanceTrendDelta === 'number'
                ? `${balanceTrendDelta >= 0 ? '+' : '-'}${formatCurrency(Math.abs(balanceTrendDelta), defaultCurrency)}`
                : '—',
            meta: balanceHistoryWindowLabel
          },
          {
            label: 'Largest position',
            value: Treasury.loading
              ? 'loading'
              : topAssetSummary
                ? formatCurrency(topAssetSummary.fiatValue, defaultCurrency)
                : 'None',
            meta: topAssetSummary ? topAssetSummary.ticker : 'No settled funds yet'
          },
          {
            label: 'Top 3 concentration',
            value: Treasury.loading
              ? 'loading'
              : totalEstimatedValue > 0
                ? `${(topThreeShare * 100).toFixed(topThreeShare >= 0.995 ? 0 : 1)}%`
                : 'None',
            meta: totalEstimatedValue > 0 ? 'Share of portfolio value' : 'No visible concentration yet'
          }
        ].map((item) =>
          <SummaryStripItem
            key={item.label}
            style={{ gap: 5 }}>
            <SummaryLabel>{item.label}</SummaryLabel>
            <SummaryValue style={{ fontSize: 16.5 }}>
              {item.value === 'loading'
                ? <LoadingBar style={{ height: 24, width: 110 }} />
                : item.value}
            </SummaryValue>
            <SummaryMeta>{item.meta}</SummaryMeta>
          </SummaryStripItem>
        )}
      </SummaryStrip>
    </Surface>

    <Surface style={{ gap: 14, padding: '16px 18px' }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 11 }}>
        <div style={{ alignItems: 'flex-start', display: 'flex', gap: 14, justifyContent: 'space-between', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <span style={{ color: 'var(--ink, #20242d)', fontSize: 12.75, fontWeight: 500 }}>Portfolio allocation</span>
            <span style={{ color: 'var(--text-soft, #7d8492)', fontSize: 12.25 }}>
              Current asset mix by estimated value in {defaultCurrency}.
            </span>
          </div>
          <div style={{ alignItems: 'center', display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <Gate anyOf={['ReadDeposit', 'ReadDepositAccount']}>
              <SecondaryButton
                onClick={() => navigate('/receive')}
                style={{ minHeight: 36 }}>
                Deposit
              </SecondaryButton>
            </Gate>
            {User.organization?.features?.isConversionEnabled && canWriteSwap &&
              <SecondaryButton
                disabled={Treasury.loading || !swappableDefaultRow}
                onClick={() => {
                  if (!swappableDefaultRow) {
                    return
                  }
                  onOpenSwap({
                    network: swappableDefaultRow.source.network ?? methodNetworkName(swappableDefaultRow.method),
                    token: swappableDefaultRow.source.token ?? methodTokenAddress(swappableDefaultRow.method),
                    asset: swappableDefaultRow.source.asset,
                    maxAmount: swappableDefaultRow.source.balance
                  })
                }}
                style={{ minHeight: 36 }}>
                Convert
              </SecondaryButton>}
            <Gate permission='WriteWithdrawal'>
              <PrimaryButton
                disabled={Treasury.loading || withdrawableBalanceAssets.length === 0}
                onClick={() => navigate('/send')}
                style={{ minHeight: 36 }}>
                Withdraw
              </PrimaryButton>
            </Gate>
          </div>
        </div>
        {!Treasury.loading && allocationRows.length > 0 &&
          <>
            <div style={{
              alignItems: 'stretch',
              background: 'var(--surface-pressed, #edf1f7)',
              borderRadius: 999,
              display: 'flex',
              height: 7,
              overflow: 'hidden'
            }}>
              {allocationRows.map((row) =>
                <div
                  key={row.ticker}
                  style={{
                    background: row.color,
                    minWidth: `${Math.max(row.share * 100, 3)}%`
                  }}
                />
              )}
            </div>
            <div style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '11px 16px'
            }}>
              {allocationRows.map((row) =>
                <div
                  key={`${row.ticker}-legend`}
                  style={{
                    alignItems: 'center',
                    display: 'flex',
                    gap: 7,
                    minWidth: 0
                  }}>
                  <div style={{
                    background: row.color,
                    borderRadius: 999,
                    flexShrink: 0,
                    height: 7,
                    width: 7
                  }} />
                  <span style={{ color: 'var(--ink, #20242d)', fontSize: 12.25, fontWeight: 500 }}>{row.ticker}</span>
                  <span style={{ color: 'var(--text-soft, #7d8492)', fontSize: 12.25 }}>
                    {formatCurrency(row.fiatValue, defaultCurrency)}
                  </span>
                  <span style={{ color: '#4b5565', fontSize: 12.25, fontWeight: 500 }}>
                    {(row.share * 100).toFixed(row.share >= 0.1 ? 0 : 1)}%
                  </span>
                </div>
              )}
            </div>
          </>}
        {!Treasury.loading && allocationRows.length === 0 &&
          <CenteredEmptyState>
            <EmptyStateTitle>{emptyAllocationTitle}</EmptyStateTitle>
            <span>{emptyAllocationDescription}</span>
          </CenteredEmptyState>}
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <span style={{ color: 'var(--ink, #20242d)', fontSize: 13.75, fontWeight: 500 }}>
          {activeBalanceScope === 'private' ? 'Private treasury balances' : 'Asset balances'}
        </span>
        <span style={{ color: 'var(--text-soft, #7d8492)', fontSize: 12.25 }}>
          {activeBalanceScope === 'private'
            ? 'Review isolated private treasury funds before withdrawing.'
            : 'Your balances by asset.'}
        </span>
      </div>
      <Table
        headers={['Asset', 'Balance', 'Exchange rate'].map(h => <span key={h}>{h}</span>)}
        borderless
        tableContainerStyle={{
          gridTemplateColumns: 'minmax(260px, 1.35fr) minmax(180px, 1fr) minmax(160px, 0.8fr)'
        }}
        items={
          groupedBalanceRows.flatMap(group => {
            // Balances are network agnostic: one row per asset, and networks
            // only surface when funds move out.
            const primary = [
              <div
                key="token"
                style={{ display: 'flex', flexDirection: 'column', fontSize: 14 }}>
                <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                  <PaymentMethodDisplay
                    method={{
                      network: group.first.source.network ?? methodNetworkName(group.first.method),
                      token: group.first.source.token ?? methodTokenAddress(group.first.method)
                    }}
                    paymentMethod={group.first.source.asset
                      ? { ...group.first.method, hostChain: undefined }
                      : group.first.method}
                    excludeName
                    size={30} />
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                    <TokenTitle>{group.first.displayName}</TokenTitle>
                  </div>
                </div>
              </div>,
              <div key="value" style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <span>
                  {formatAssetAmount(group.totalBalance, group.first.ticker)}
                </span>
                {group.conversion || group.anyFiat
                  ? <span style={{ opacity: 0.5 }}>
                      ≈ {formatCurrency(group.totalFiatValue, defaultCurrency)}
                  </span>
                  : <LoadingBar style={{ height: 10 }} />}
              </div>,

              <div key='conversion'>
                {group.conversion
                  ? <span>{formatCurrency(Number(group.conversion.value), defaultCurrency)} {defaultCurrency}</span>
                  : group.anyFiat
                    ? <span>1 {defaultCurrency}</span>
                    : <LoadingBar style={{ height: 16 }} />}
              </div>
            ]
            return [primary]
          })
        }
        isLoading={Treasury.loading}
        emptyPlaceholderText={activeBalanceScope === 'private'
          ? 'No private treasury funds yet.'
          : 'No funds yet.'}
      />
    </Surface>

    {
      exportBalanceModalOpen &&
      <Modal visible={exportBalanceModalOpen} onClose={() => setExportBalanceModalOpen(false)} title='Export balance report' innerStyles={{ width: 820 }}>
        <ExportBalanceForm />
      </Modal>
    }

    {
      swapModalOpen && assetToConvertFrom &&
      <Modal
        visible={swapModalOpen}
        onClose={() => setSwapModalOpen(false)}
        presentation='drawer'
        title='Convert balances'>
        {swapStep === 'compose' &&
          <SwapStepPanel key='compose'>
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 9,
              padding: 10,
              borderRadius: 13,
              border: '1px solid var(--border-soft, #eceff3)',
              background: 'var(--surface-subtle, #fcfdff)'
            }}>
              <div style={{
                borderRadius: 13,
                padding: 12,
                backgroundColor: 'var(--surface, #ffffff)',
                border: '1px solid var(--border, #e7ebf2)',
                display: 'flex',
                flexDirection: 'column',
                gap: 9
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
                  <span style={{ fontSize: 11.5, color: 'var(--text-muted, #6b7280)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>From</span>
                  <span style={{ fontSize: 12.25, color: hasExecutableSwapBalance ? 'var(--text-muted, #6b7280)' : '#f59e0b' }}>
                    Available: {formatAssetAmount(swapFromBalance, swapFromTicker)} {swapFromMethod?.ticker}
                  </span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                  <input
                    ref={swapAmountInputRef}
                    value={swapAmount}
                    onChange={async (e: React.ChangeEvent<HTMLInputElement>) => {
                      const raw = e.target.value
                      let clamped = raw

                      setSwapStep('compose')
                      setSwapEstimate(null)
                      setSwapEstimateMessage('')

                      if (!raw) {
                        setSwapAmount('')
                        return
                      }

                      if (compareAssetAmountStrings(raw, swapFromBalance || '0') > 0) {
                        clamped = maxSwappableAmount
                      }

                      setSwapAmount(clamped)
                      await recalculateSwapEstimate(clamped, selectedSwapInto)
                    }}
                    placeholder='0.0'
                    style={{
                      border: 'none',
                      background: 'transparent',
                      color: 'var(--ink, #20242d)',
                      fontSize: 23,
                      fontWeight: 600,
                      lineHeight: 1.06,
                      outline: 'none',
                      padding: 0,
                      flex: 1,
                      minWidth: 80
                    }}
                  />
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                    <SecondaryButton
                      type='button'
                      disabled={!hasExecutableSwapBalance}
                      onClick={async () => {
                        const clamped = maxSwappableAmount
                        setSwapStep('compose')
                        setSwapAmount(clamped)
                        setSwapEstimate(null)
                        setSwapEstimateMessage('')
                        await recalculateSwapEstimate(clamped, selectedSwapInto)
                      }}
                      style={{ minHeight: 34, padding: '0 12px' }}>
                      Max
                    </SecondaryButton>
                    <Dropdown
                      preferredAlignment='right'
                      overflow='auto'
                      outerStyle={{ minHeight: 38, minWidth: 146, padding: '7px 10px', borderRadius: 10 }}
                      innerStyle={{ width: 280, maxHeight: 330, borderRadius: 12 }}
                      selectedContent={
                        swapFromMethod
                          ? renderSwapMethodOption(swapFromMethod)
                          : <span style={{ fontSize: 13.5, color: 'var(--icon-muted, #747894)' }}>Select asset</span>
                      }
                      dropdownContent={
                        <div>
                          <DropdownContentHeader style={{
                            position: 'sticky',
                            top: 0,
                            background: 'var(--surface, #ffffff)',
                            borderBottom: '1px solid var(--surface-pressed, #edf1f7)',
                            padding: '2px 2px 0',
                            zIndex: 1
                          }}>
                            <DropdownTextInput
                              autoFocus
                              value={swapSourceQuery}
                              onChange={(e) => setSwapSourceQuery((e.target as HTMLInputElement).value)}
                              placeholder='Search asset or ticker'
                            />
                          </DropdownContentHeader>
                          {filteredSwapSourceMethods.map((m, idx) => (
                            <DropdownItem
                              key={`${m.name}-${m.ticker}-${idx}`}
                              onClick={async () => {
                                const nextSource = managedMethodToPaymentMethod(m)
                                const nextBalance =
                                  balanceRows.find((row) => balanceMatchesPaymentMethod(row.source, nextSource))?.source.balance ?? '0'
                                const nextSourceIsStable = isStableSwapTicker(m.ticker)
                                const nextTarget = selectedSwapInto &&
                                  !isSamePaymentMethod(managedMethodToPaymentMethod(selectedSwapInto), nextSource) &&
                                  (
                                    isMultiLegSwapEnabled ||
                                      nextSourceIsStable ||

                                      isStableSwapTicker(selectedSwapInto.ticker)
                                  )
                                  ? selectedSwapInto
                                  : null

                                setSwapStep('compose')
                                setAssetToConvertFrom(nextSource)
                                setSwapFromBalance(String(nextBalance))
                                setSelectedSwapInto(nextTarget)
                                setSwapEstimate(null)
                                setSwapEstimateMessage('')
                                setSwapSourceQuery('')
                                setSwapTargetQuery('')

                                if (nextTarget) {
                                  await recalculateSwapEstimate(swapAmount, nextTarget, nextSource)
                                }
                              }}
                              style={{ padding: '9px 10px', borderBottom: '1px solid var(--hairline-soft, #f1f3f7)' }}>
                              {renderSwapMethodOption(m)}
                            </DropdownItem>
                          ))}
                          {filteredSwapSourceMethods.length === 0 &&
                            <div style={{ color: 'var(--icon-muted, #747894)', fontSize: 13, padding: '14px 12px' }}>
                              {eligibleSwapSourceMethods.length === 0
                                ? `No assets with at least ${formatCurrency(MIN_SWAP_SOURCE_FIAT_VALUE, defaultCurrency)} available to swap.`
                                : 'No matching assets found.'}
                            </div>}
                        </div>
                      }
                    />
                  </div>
                </div>
              </div>

              <div style={{ display: 'flex', justifyContent: 'center', margin: '-6px 0' }}>
                <Tooltip
                  disabled={!!selectedSwapInto}
                  message='Choose the asset you want to receive first.'>
                  <span style={{ display: 'inline-flex' }}>
                    <SwapFlipButton
                      type='button'
                      disabled={!selectedSwapInto}
                      $spinning={flipSpinning}
                      onAnimationEnd={() => setFlipSpinning(false)}
                      onClick={() => {
                        setFlipSpinning(true)
                        void flipSwapPair()
                      }}>
                      <SwapTransactionIcon />
                    </SwapFlipButton>
                  </span>
                </Tooltip>
              </div>

              <div style={{
                borderRadius: 13,
                padding: 12,
                backgroundColor: 'var(--surface, #ffffff)',
                border: '1px solid var(--border, #e7ebf2)',
                display: 'flex',
                flexDirection: 'column',
                gap: 9
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
                  <span style={{ fontSize: 11.5, color: 'var(--text-muted, #6b7280)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>To</span>
                  {swapEstimating &&
                    <span style={{ fontSize: 12, color: 'var(--text-muted, #6b7280)' }}>Refreshing quote…</span>}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                  <SwapEstimatedValue $pulsing={swapEstimating}>
                    {swapEstimatedAmount != null
                      ? formatAssetAmount(swapEstimatedAmount, selectedSwapInto?.ticker)
                      : '--'}
                  </SwapEstimatedValue>
                  <Dropdown
                    preferredAlignment='right'
                    overflow='auto'
                    outerStyle={{ minHeight: 49, minWidth: 146, padding: '7px 10px', borderRadius: 10 }}
                    innerStyle={{ width: 280, maxHeight: 330, borderRadius: 12 }}
                    selectedContent={
                      selectedSwapInto
                        ? renderSwapMethodOption(selectedSwapInto)
                        : <span style={{ fontSize: 13.5, color: 'var(--icon-muted, #747894)' }}>Select asset</span>
                    }
                    dropdownContent={
                      <div>
                        <DropdownContentHeader style={{
                          position: 'sticky',
                          top: 0,
                          background: 'var(--surface, #ffffff)',
                          borderBottom: '1px solid var(--surface-pressed, #edf1f7)',
                          padding: '2px 2px 0',
                          zIndex: 1
                        }}>
                          <DropdownTextInput
                            autoFocus
                            value={swapTargetQuery}
                            onChange={(e) => setSwapTargetQuery((e.target as HTMLInputElement).value)}
                            placeholder='Search asset or ticker'
                          />
                        </DropdownContentHeader>
                        {filteredSwapTargetMethods.map((m, idx) => (
                          <DropdownItem
                            key={`${m.name}-${m.ticker}-${idx}`}
                            onClick={async () => {
                              setSwapStep('compose')
                              setSelectedSwapInto(m)
                              setSwapEstimate(null)
                              setSwapEstimateMessage('')
                              setSwapTargetQuery('')
                              await recalculateSwapEstimate(swapAmount, m)
                            }}
                            style={{ padding: '9px 10px', borderBottom: '1px solid var(--hairline-soft, #f1f3f7)' }}>
                            {renderSwapMethodOption(m)}
                          </DropdownItem>
                        ))}
                        {filteredSwapTargetMethods.length === 0 &&
                          <div style={{ color: 'var(--icon-muted, #747894)', fontSize: 13, padding: '14px 12px' }}>
                            No matching assets found.
                          </div>}
                      </div>
                    }
                  />
                </div>
              </div>
            </div>

            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 9,
              padding: '10px 13px',
              borderRadius: 13,
              border: '1px solid var(--border-soft, #eceff3)',
              background: 'var(--surface-subtle, #f9fafb)'
            }}>
              {swapEstimate && selectedSwapInto && swapFromMethod
                ? <SwapQuoteBreakdown
                  quote={swapEstimate}
                  fromTicker={swapFromMethod.ticker}
                  toTicker={selectedSwapInto.ticker}
                  fromAmount={swapAmount}
                />
                : <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10 }}>
                  <span style={{ color: 'var(--text-muted, #5f6675)', fontSize: 12.5 }}>Rate</span>
                  <span style={{ color: 'var(--ink, #20242d)', fontSize: 13, fontWeight: 500 }}>
                    {swapEstimateMessage || '--'}
                  </span>
                </div>}
            </div>

            <PrimaryButton
              disabled={!canReviewSwap}
              style={{ width: '100%' }}
              onClick={() => setSwapStep('confirm')}>
              Review conversion
            </PrimaryButton>
          </SwapStepPanel>}

        {swapStep === 'confirm' &&
          <SwapStepPanel key='confirm'>
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 9,
              padding: '13px',
              borderRadius: 13,
              border: '1px solid var(--border-soft, #eceff3)',
              background: 'var(--surface-subtle, #fcfdff)'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                <span style={{ color: 'var(--text-muted, #5f6675)', fontSize: 12.5 }}>You pay</span>
                <span style={{ color: 'var(--ink, #20242d)', fontSize: 13.5, fontWeight: 500 }}>
                  {formatAssetAmount(swapAmount, swapFromTicker)} {swapFromMethod?.ticker}
                </span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                <span style={{ color: 'var(--text-muted, #5f6675)', fontSize: 12.5 }}>You receive</span>
                <span style={{ color: 'var(--ink, #20242d)', fontSize: 13.5, fontWeight: 500 }}>
                  {swapEstimatedAmount != null && selectedSwapInto
                    ? `${formatAssetAmount(swapEstimatedAmount, selectedSwapInto.ticker)} ${selectedSwapInto.ticker}`
                    : '--'}
                </span>
              </div>
              {swapEstimate && selectedSwapInto && swapFromMethod &&
                <SwapQuoteBreakdown
                  quote={swapEstimate}
                  fromTicker={swapFromMethod.ticker}
                  toTicker={selectedSwapInto.ticker}
                />}
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                <span style={{ color: 'var(--text-muted, #5f6675)', fontSize: 12.5 }}>Available balance</span>
                <span style={{ color: 'var(--ink, #20242d)', fontSize: 13.5, fontWeight: 500 }}>
                  {formatAssetAmount(swapFromBalance, swapFromTicker)} {swapFromMethod?.ticker}
                </span>
              </div>
            </div>

            <div style={{
              padding: '10px 13px',
              borderRadius: 13,
              border: '1px solid var(--border-soft, #eceff3)',
              background: 'var(--surface-subtle, #f9fafb)',
              color: 'var(--text-muted, #5f6675)',
              fontSize: 12.25,
              lineHeight: 1.5
            }}>
              Amounts can move slightly until the swap executes.
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 8 }}>
              <SecondaryButton
                onClick={() => setSwapStep('compose')}
                style={{ width: '100%' }}>
                Back
              </SecondaryButton>
              <PrimaryButton
                isLoading={swapLoading}
                disabled={!canReviewSwap}
                style={{ width: '100%' }}
                onClick={async () => {
                  if (!assetToConvertFrom || !selectedSwapInto || !swapEstimate) return
                  setSwapLoading(true)
                  try {
                    await performSwap({
                      fromNetwork: assetToConvertFrom.network,
                      fromToken: assetToConvertFrom.token,
                      toNetwork: selectedSwapInto.hostChain?.name ?? selectedSwapInto.name,
                      toToken: selectedSwapInto.hostChain?.tokenAddress ?? undefined,
                      amount: Number(swapAmount)
                    })
                    successToast('Conversion complete. Funds are in your balance')
                    setSwapModalOpen(false)
                    Treasury.loadBalances()
                  } catch (err) {
                    errorToast(err.response?.data?.error || err.message)
                  } finally {
                    setSwapLoading(false)
                  }
                }}>
                Confirm conversion
              </PrimaryButton>
            </div>
          </SwapStepPanel>}
      </Modal>
    }
  </DashboardPage>
})

export default Overview
